import { ActionGroupTypes, ActionStatusTypes } from "../../../enum/ActionStatusTypes";
import * as logger from "../../../logger";
import { getNodeById } from "../../../nodeSelector";
import * as nodeUtils from "../../../utils/nodeUtils";
import * as pathUtils from "../../../utils/pathUtils";
import * as systemUtils from "../../../utils/systemUtils";
import { ACTION_PREFIX_PARAMETER } from "./abstract/parameterAbstract";
import {
  getParameterReferences,
  processParameterReferences,
  trimIdReference,
} from "./helpers/parameterHelper";

const _action = `${ACTION_PREFIX_PARAMETER}_update_name`;
const _header = `Rename parameter`;

export const updateParameterName = (root, id, name, lastPayload) => {
  const _payload = updateParameterNameLogic(root, id, name, lastPayload);

  logger.logAction(_action, _payload, ACTION_PREFIX_PARAMETER);

  return { root, info: _payload };
};

//--------------------------------------------------------------------------------

export const updateParameterNameLogic = (root, id, name, lastPayload) => {
  const { data: _node, index: _index } = getNodeById(root, id);

  if (!_node) {
    throw new Error("Parameter to update not found");
  }

  if (nodeUtils.getName(_node) === name) {
    return generatePayloadIndifferent();
  }

  const { data: _nodeParent } = getNodeById(root, pathUtils.getIdParent(id));

  const _nodeBackup = systemUtils.createDeepCopy(_node);
  const _references = getParameterReferences(root, _node);

  nodeUtils.setName(_node, name, true);

  handleReferences(root, _references);

  return generatePayload(
    _nodeBackup,
    _index,
    _nodeParent,
    _references.description,
    nodeUtils.getName(_node),
    lastPayload
  );
};

const handleReferences = (root, references) => {
  const funTarget = (e) => {
    nodeUtils.addIdReference(e.dst, nodeUtils.getID(e.src));
    trimIdReference(root, e.dst);
  };

  const funReference = (e) => {
    nodeUtils.setIdTarget(e.src, nodeUtils.getID(e.dst));
  };

  processParameterReferences(references, funTarget, funTarget, funReference, funReference);
};

//--------------------------------------------------------------------------------

const generatePayload = (node, index, nodeParent, uuidReferences, nameAfter, lastPayload) => {
  if (!nodeUtils.getName(node)) {
    return generatePayloadCreate(node, index, nodeParent, uuidReferences, nameAfter, lastPayload);
  }

  return generatePayloadUpdate(node, index, nodeParent, uuidReferences, nameAfter)
}

const generatePayloadCreate = (node, index, nodeParent, uuidReferences, nameAfter, previousAction) => {
  const _payload = {
    group: ActionGroupTypes.ADD,
    status: ActionStatusTypes.SUCCESS,
    action: _action,
    time: systemUtils.getTimestamp(),
    header: _header,
    description: `${previousAction.description} Name: '${nameAfter}'.`,
    node: node,
    uuid: nodeUtils.getUUID(node),
    uuidParent: nodeUtils.getUUID(nodeParent),
    index: index,
    nameBefore: nodeUtils.getName(node),
    nameAfter: nameAfter,
  }

  return _payload;
}

const generatePayloadUpdate = (node, index, nodeParent, uuidReferences, nameAfter) => {
  const _payload = {
    group: ActionGroupTypes.UPDATE,
    status: ActionStatusTypes.SUCCESS,
    action: _action,
    time: systemUtils.getTimestamp(),
    header: _header,
    description: generatePayloadDescription(node, nodeParent, nameAfter),
    node: node,
    uuid: nodeUtils.getUUID(node),
    uuidParent: nodeUtils.getUUID(nodeParent),
    index: index,
    nameBefore: nodeUtils.getName(node),
    nameAfter: nameAfter,
  };

  return { ..._payload, ...uuidReferences };
};

const generatePayloadIndifferent = () => {
  const _payload = {
    status: ActionStatusTypes.INDIFFERENT,
    action: _action,
    time: systemUtils.getTimestamp()
  }

  return _payload;
}

const generatePayloadDescription = (node, nodeParent, nameAfter) => {

  if (nodeUtils.isRoot(nodeParent)) {
    return `Rename top global parameter '${nodeUtils.getName(node)}' to '${nameAfter}'.`;
  }

  if (nodeUtils.isParameterGlobal(nodeParent)) {
    return `Rename nested global parameter '${nodeUtils.getName(node)}' to '${nameAfter}'.`;
  }

  if (nodeUtils.isFunction(nodeParent)) {
    return `Rename parameter '${nodeUtils.getName(node)}' to '${nameAfter}'.`;
  }

  return `Rename nested parameter '${nodeUtils.getName(node)}' to '${nameAfter}'.`;
}
